import { InformationRequestStatus } from "@tfg/db"
import { createContext, useContext, ReactNode, useEffect } from "react"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { updateOperationsValidationType } from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.request.dto"

import { OperationNotCreated } from "~/pages/SupplierPortal/TraceabilityRequests/components/OperationNotCreated"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"

import { useUpdateOperationsMutation } from "../hooks"
import {
  RequestWithOpItem,
  OperationItem,
  allChildrenItems,
  nodeRequiresRef,
  calculateOperationProgress,
} from "../utils"

type TraceabilityOperationsContextType = {
  request: RequestWithOpItem
  status: InformationRequestStatus
  operationItems: OperationItem[]
  updateOperationAndChildren: (
    update: updateOperationsValidationType["update"]
  ) => void
  isTranfering: boolean
  isReinforcedTraceability: boolean
  operationItem: OperationItem
  isTransferred: boolean
  currentContactId: number
  user: LibraryResponses.TFGUser
  isAdmin: boolean
  mandatoryReference: boolean
}

const TraceabilityOperationsContext =
  createContext<TraceabilityOperationsContextType | null>(null)

export function TraceabilityOperationsProvider({
  children,
  request,
  status,
  operationItems,
  operationItem,
  setProgress,
}: {
  children: ReactNode
  request: RequestWithOpItem
  status: InformationRequestStatus
  operationItems: OperationItem[]
  operationItem: OperationItem
  setProgress: (operationId: number, value: number) => void
}) {
  const { user, isAdmin } = useInformationRequestContext()
  const isReinforcedTraceability =
    request.original.reinforcedTraceability && operationItem.rank >= 2
  const currentContactId = request.original.contactId
  const isTransferred = getOperationItemIsTransferred(
    operationItem,
    operationItem.operation?.owner.id,
    isReinforcedTraceability
  )
  const { customFieldValues } = useInformationRequestContext()

  useEffect(() => {
    setProgress(
      operationItem.operation?.id ?? 0,
      calculateOperationProgress(
        isTransferred,
        operationItem,
        operationItems,
        customFieldValues
      )
    )
  }, [
    operationItem,
    customFieldValues,
    isTransferred,
    operationItems,
    setProgress,
  ])

  const mandatoryReference = nodeRequiresRef(
    operationItem.parentOperation ?? null,
    operationItem.operation ?? null
  )

  const { mutate: updateOperationsMutation, isLoading: isTranfering } =
    useUpdateOperationsMutation(request.original.id)

  const updateOperationAndChildren = (
    update: updateOperationsValidationType["update"]
  ) => {
    if (shouldSkipUpdate(update.contactId, currentContactId)) {
      return
    }

    const eligibleOperations = getEligibleOperations(
      operationItem,
      operationItems
    )
    const operationIds = filterOperationIds(
      eligibleOperations,
      update,
      isReinforcedTraceability
    )

    if (operationIds.length > 0) {
      updateOperationsMutation({ ids: operationIds, update })
    }
  }

  if (!user?.id) {
    return null
  }

  if (!operationItem.operation) {
    return <OperationNotCreated operationItem={operationItem} />
  }

  return (
    <TraceabilityOperationsContext.Provider
      value={{
        request,
        status,
        operationItems,
        updateOperationAndChildren,
        isTranfering,
        isReinforcedTraceability,
        operationItem,
        isTransferred,
        currentContactId,
        user,
        isAdmin,
        mandatoryReference,
      }}
    >
      {children}
    </TraceabilityOperationsContext.Provider>
  )
}

export const useTraceabilityOperations = () => {
  const context = useContext(TraceabilityOperationsContext)
  if (!context) {
    throw new Error(
      "useTraceabilityOperations must be used within a TraceabilityOperationsProvider"
    )
  }
  return context
}

export const getOperationItemIsTransferred = (
  operationItem: OperationItem,
  operationOwnerId: string | undefined,
  isReinforcedTraceability: boolean
) => {
  const contact = operationItem.operation?.contact
  return (
    (contact &&
      (contact.userId !== operationOwnerId || isReinforcedTraceability)) ||
    !operationItem.isInInformationRequest
  )
}

const shouldSkipUpdate = (newContactId?: number, currentContactId?: number) => {
  return newContactId === currentContactId
}

const getEligibleOperations = (
  currentOperation: OperationItem,
  allOperations: OperationItem[]
): OperationItem[] => {
  return [
    currentOperation,
    ...allChildrenItems(allOperations, currentOperation.nodeId),
  ]
}

const filterOperationIds = (
  operations: OperationItem[],
  update: updateOperationsValidationType["update"],
  isReinforcedTraceability: boolean
): number[] => {
  if (operations.length === 0) {
    return []
  }
  return operations.flatMap((operation: OperationItem): number[] => {
    if (
      !operation.operation ||
      operation.operation.productionSiteId ||
      !isValidOperation(operation)
    ) {
      return []
    }

    if (isReinforcedTraceability) {
      const id = handleReinforcedTraceability(operation, update)
      if (id) {
        return [id]
      }
    }

    return [operation.operation.id]
  })
}

const isValidOperation = (operation: OperationItem): boolean => {
  return !!(operation.operation && !operation.operation.productionSiteId)
}

const handleReinforcedTraceability = (
  operation: OperationItem,
  update: updateOperationsValidationType["update"]
): number | null => {
  if (!operation.operation || update.doNotKnow !== undefined) {
    return null
  }
  return operation.operation.id
}
